import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, UserPlus, Share2, Gift } from 'lucide-react';
import { NAV_CATEGORIES } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { getJourneyId, trackEvent, trackPageLifecycle } from '../lib/customerEventTracker';

const Referral = () => {
  const { t } = useLanguage();
  useEffect(
    () => trackPageLifecycle({
      funnel_name: 'REFERRAL_FORM',
      form_name: 'ReferralForm',
      open_event_name: 'referral_form_open',
    }),
    [],
  );
  const steps = useMemo(() => [
    { icon: UserPlus, title: t('ref_form.step_join_title', 'Join the program'), desc: t('ref_form.step_join_desc', 'Register yourself as a referrer with SolveTax. It takes less than a minute.') },
    { icon: Share2, title: t('ref_form.step_share_title', 'Share with friends'), desc: t('ref_form.step_share_desc', 'Share your Referral Phone Number with friends, family and business associates.') },
    { icon: Gift, title: t('ref_form.step_earn_title', 'Earn rewards'), desc: t('ref_form.step_earn_desc', 'Receive ₹200 cashback for every successful referral that files a return with us.') },
  ], [t]);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || process.env.VITE_API_BASE_URL || process.env.VITE_API_URL || '';
  const PUBLIC_API_KEY = process.env.REACT_APP_PUBLIC_API_KEY || process.env.VITE_PUBLIC_API_KEY || '';
  const API_TIMEOUT_MS = 15000;
  const [data, setData] = useState({
    your_name: '',
    phone_number: '',
    referal_phone_number: '',
    email_address: '',
    service_required: '',
    your_message: '',
  });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setData({
      your_name: '',
      phone_number: '',
      referal_phone_number: '',
      email_address: '',
      service_required: '',
      your_message: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.your_name.trim()) {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Name is required',
      });
      toast.error(t('ref_form.err_name', 'Your name is required.'));
      return;
    }
    if (!/^[0-9]{10}$/.test(data.phone_number)) {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Phone number must be valid',
      });
      toast.error(t('ref_form.err_phone', 'Phone number must be a valid 10-digit mobile number.'));
      return;
    }
    if (!/^[0-9]{10}$/.test(data.referal_phone_number)) {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Referral phone must be valid',
      });
      toast.error(t('ref_form.err_ref_phone', 'Referral phone number must be a valid 10-digit mobile number.'));
      return;
    }
    if (!data.service_required) {
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        error_code: 'validation_error',
        error_message: 'Service required is missing',
      });
      toast.error(t('ref_form.err_service', 'Service required for referral is mandatory.'));
      return;
    }
    const payload = {
      your_name: data.your_name.trim(),
      phone_number: data.phone_number.trim(),
      email_address: data.email_address.trim() || null,
      service_required: data.service_required,
      referal_phone_number: data.referal_phone_number.trim(),
      your_message: data.your_message.trim() || null,
    };

    setLoading(true);
    trackEvent({
      event_name: 'referral_submit_attempt',
      event_action: 'submit',
      event_status: 'attempt',
      event_category: 'form',
      form_name: 'ReferralForm',
      funnel_name: 'REFERRAL_FORM',
      funnel_step_number: 2,
      funnel_step_name: 'submit',
      journey_id: getJourneyId('REFERRAL_FORM'),
      phone_number: payload.phone_number,
      service_code: payload.service_required,
    });
    try {
      if (API_BASE) {
        await axios.post(`${API_BASE}/api/v1/contact-support`, payload, {
          timeout: API_TIMEOUT_MS,
          headers: {
            'Content-Type': 'application/json',
            ...(PUBLIC_API_KEY ? { 'X-Public-Api-Key': PUBLIC_API_KEY } : {}),
          },
        });
      }
      trackEvent({
        event_name: 'referral_submit_success',
        event_action: 'submit',
        event_status: 'success',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM', { reset: true }),
        phone_number: payload.phone_number,
        service_code: payload.service_required,
        api_name: '/api/v1/contact-support',
        api_status_code: 201,
      });
      toast.success(t('ref_form.toast_ok', 'Referral request submitted. Our support team will contact you within 2 hours.'));
      resetForm();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = detail?.error?.message || (typeof detail === 'string' ? detail : null) || err?.message || 'Request failed';
      trackEvent({
        event_name: 'referral_submit_failed',
        event_action: 'submit',
        event_status: 'failed',
        event_category: 'form',
        form_name: 'ReferralForm',
        funnel_name: 'REFERRAL_FORM',
        funnel_step_number: 2,
        funnel_step_name: 'submit',
        journey_id: getJourneyId('REFERRAL_FORM'),
        phone_number: payload.phone_number,
        service_code: payload.service_required,
        api_name: '/api/v1/contact-support',
        api_status_code: status || null,
        error_code: status ? `api_${status}` : 'api_error',
        error_message: msg,
      });
      if (status === 401) toast.error(t('contact.toast_unauthorized', 'Unauthorized: missing or invalid public API key.'));
      else if (status === 429) toast.error(t('contact.toast_rate', 'Rate limit exceeded. Please try again in a moment.'));
      else if (status === 422) toast.error(t('contact.toast_validation', 'Validation: {msg}', { msg }));
      else toast.error(t('contact.toast_backend', 'Backend: {msg}', { msg }));
    } finally {
      setLoading(false);
    }
  };

  return (
  <div className="pt-28">
    <section className="py-14 hero-gradient">
      <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h1 className="ui-title-xl mb-3">
            {t('ref_form.hero_title', 'Our')} <span className="text-[#14A098]">{t('ref_form.hero_accent', 'Referral Program')}</span>
          </h1>
          <p className="ui-body">{t('ref_form.hero_sub', 'Fill out the form and our support team will contact you within 2 hours.')}</p>
      </div>
    </section>

      <section className="py-14 bg-teal-50/20">
        <div className="w-full max-w-[1680px] mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-4 space-y-4 order-2 lg:order-1">
            <div className="rounded-2xl bg-white border border-gray-100 shadow-lg p-5 md:p-7 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-md mb-5">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <div className="text-5xl md:text-7xl font-extrabold text-[#0f2540] leading-none">₹200</div>
              <div className="text-sm md:text-lg uppercase tracking-wider text-[#14A098] font-semibold mt-2">{t('referral.reward_label', 'Reward per referral')}</div>
              <p className="text-gray-600 mt-3 text-sm md:text-base">{t('ref_form.reward_line', 'Users get ₹200 cashback for every successful referral.')}</p>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white shadow-lg p-4 md:p-5">
              <h3 className="text-lg font-bold text-[#0f2540] mb-4">{t('ref_form.how_title', 'How Referral Works')}</h3>
              <div className="space-y-3">
                {steps.map((s, i) => (
                  <div key={s.title} className="rounded-xl border border-teal-100 bg-teal-50/40 p-3 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#14A098] to-[#5eead4] flex items-center justify-center shadow-sm shrink-0">
                      <s.icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#14A098] mb-0.5">{t('common.step_label', 'Step {n}', { n: i + 1 })}</div>
                      <h4 className="text-base font-bold text-[#0f2540] leading-tight">{s.title}</h4>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{s.desc}</p>
                    </div>
          </div>
        ))}
      </div>
            </div>
          </div>
          <div className="lg:col-span-8 order-1 lg:order-2">
            <Card id="referral-form" className="p-6 md:p-8 rounded-2xl border border-gray-100 shadow-lg bg-white">
              <h2 className="text-2xl font-bold text-[#0f2540] mb-1">{t('ref_form.form_title', 'Submit Referral')}</h2>
              <p className="text-sm text-gray-500 mb-6">{t('ref_form.form_sub', 'Fill out the form and our support team will contact you within 2 hours.')}</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ref-your_name" className="text-sm mb-2 block">{t('ref_form.label_name', 'Your Name')} <span className="text-rose-500">*</span></Label>
                    <Input
                      id="ref-your_name"
                      name="your_name"
                      autoComplete="name"
                      value={data.your_name}
                      onChange={(e) => setData({ ...data, your_name: e.target.value })}
                      placeholder={t('contact.ph_name', 'Full name')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ref-phone_number" className="text-sm mb-2 block">{t('ref_form.label_phone', 'Phone Number')} <span className="text-rose-500">*</span></Label>
                    <Input
                      id="ref-phone_number"
                      name="phone_number"
                      autoComplete="tel"
                      value={data.phone_number}
                      onChange={(e) => setData({ ...data, phone_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder={t('contact.ph_phone', '10-digit mobile')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ref-referal_phone_number" className="text-sm mb-2 block">{t('ref_form.label_ref_phone', 'Referral phone number')} <span className="text-rose-500">*</span></Label>
                    <Input
                      id="ref-referal_phone_number"
                      name="referal_phone_number"
                      autoComplete="tel"
                      value={data.referal_phone_number}
                      onChange={(e) => setData({ ...data, referal_phone_number: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder={t('ref_form.ph_ref_phone', '10-digit referral mobile')}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ref-email_address" className="text-sm mb-2 block">{t('ref_form.label_email', 'Email Address')}</Label>
                    <Input
                      id="ref-email_address"
                      name="email_address"
                      autoComplete="email"
                      type="email"
                      value={data.email_address}
                      onChange={(e) => setData({ ...data, email_address: e.target.value })}
                      placeholder={t('contact.ph_email', 'you@email.com')}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="ref-service_required" className="text-sm mb-2 block">{t('ref_form.label_service', 'Service required for referral')} <span className="text-rose-500">*</span></Label>
                    <Select value={data.service_required} onValueChange={(v) => setData({ ...data, service_required: v })}>
                      <SelectTrigger id="ref-service_required" name="service_required" aria-label={t('ref_form.label_service', 'Service required for referral')}>
                        <SelectValue placeholder={t('contact.ph_service', 'Select a service')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {NAV_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.category} value={cat.label}>{t(`category.${cat.category}`, cat.label)}</SelectItem>
                        ))}
                        <SelectItem value="Other Services">{t('contact.other_services', 'Other Services')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="ref-your_message" className="text-sm mb-2 block">{t('ref_form.label_message', 'Your Message')}</Label>
                  <Textarea
                    id="ref-your_message"
                    name="your_message"
                    rows={5}
                    value={data.your_message}
                    onChange={(e) => setData({ ...data, your_message: e.target.value })}
                    placeholder={t('contact.ph_message', 'Tell us about your requirement')}
                  />
                </div>

                <div className="flex flex-wrap gap-3 pt-2 items-center">
                  <Button type="submit" disabled={loading} size="lg" className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full px-8 h-12 shadow-md min-w-[200px]">
                    {loading ? t('contact.btn_sending', 'Sending...') : (<>{t('form.submit_details', 'Submit Details')} <Send className="w-4 h-4 ml-2" /></>)}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-full h-12 border-gray-300">
                    {t('contact.btn_reset', 'Reset')}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2 w-full">{t('contact.legal', 'By submitting, you agree to our Terms & Privacy Policy.')}</p>
                </div>
              </form>
            </Card>
        </div>
      </div>
    </section>
  </div>
);
};

export default Referral;
